import React, { useState } from "react";
import { Alert, Box, Button, Chip, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { api } from "./api";
import { CardFooter, ErrorBanner, FieldLabel, GhostButton, PrimaryButton, StepCard, SubHeading } from "./formParts";
import { Icon, T } from "./signupDesign";

/**
 * The wizard opened by a super admin or an agent from the admin panel
 * (backend utils/merchantInvites.js). They fill in the same steps as the
 * merchant, set this restaurant's fees on Fees & terms, and send it to the
 * merchant to review and sign: the owner accepts the authorizations and
 * signs. A super admin's fees apply at once. An agent's apply at once too
 * when the order percentages, third-party % and service fee are at or above
 * their minimums (the standard) and nothing else changed; otherwise they go
 * to a super admin, and the application goes out once they're approved.
 */

const FIELDS = [
  ["deliveryCommissionRate", "Delivery orders", "percent"],
  ["selfDeliveryCommissionRate", "Self-delivery orders", "percent"],
  ["pickupCommissionRate", "Pickup orders", "percent"],
  ["thirdPartyPercent", "Third-party orders we deliver", "percent"],
  ["serviceFee", "Service fee (customers pay, per order)", "money"],
  ["deliveryFeeMin", "Delivery fee (minimum)", "money"],
  ["minimumOrder", "Minimum order amount", "money"],
  ["returnedPaymentFee", "Returned payment fee", "money"],
  ["statementFee", "Monthly statement / account fee", "money"],
  ["processingPercent", "Payment processing", "percent"],
  ["processingFixed", "+ per transaction", "money"],
];

// Their standard is a minimum: an agent may go higher on their own, lower needs a super admin.
const FLOOR = ["deliveryCommissionRate", "selfDeliveryCommissionRate", "pickupCommissionRate", "thirdPartyPercent", "serviceFee"];
const ORDER = FLOOR.slice(0, 3);
const PLATFORM = ["thirdPartyPercent", "serviceFee"];

const num = (v) => Number(String(v == null ? "" : v).replace(/[$,%\s]/g, ""));
const show = (kind, v) => (kind === "percent" ? `${Number(Number(v).toFixed(3))}%` : `$${Number(v).toFixed(2)}`);
const day = (v) => (v ? new Date(v).toLocaleString() : "");

const PRICING = {
  STANDARD: ["Standard fees", "#f1f5f9", "#475569"],
  CUSTOM: ["Own fees", "#fff7ed", "#9a3412"],
  PENDING: ["Waiting for a super admin to approve", "#fef3c7", "#92400e"],
  REJECTED: ["Fees not approved", "#fee2e2", "#991b1b"],
};

/** Who is filling this in, shown above every step. */
export function StaffBanner({ staff, starting }) {
  const who = staff ? <><strong>{staff.name}</strong>, {staff.role === "ADMIN" ? "super admin" : "agent"}</> : "RushMyFood staff";
  return (
    <Alert severity="info" icon={<Icon.Info size={20} />} sx={{ mb: 3 }}>
      <strong>Filling in for the restaurant</strong> as {who}.{" "}
      {starting
        ? "Start with step 1; nothing is sent to the restaurant until you send it for signature on the last step."
        : "Fill in what you can, set the fees on Fees & terms, then send it to the restaurant to review and sign. Only the owner can accept the terms and sign."}
    </Alert>
  );
}

/** Fees & terms for staff: this restaurant's own fees instead of the owner's authorizations. */
export function StaffFeesStep({ view, token, onSaved, onNext, onBack }) {
  const s = view.staff;
  const saved = s.ownFees || s.standardFees;
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, String(saved[k])])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fees = Object.fromEntries(FIELDS.map(([k]) => [k, num(form[k])]));
  // A restaurant's service fee is $0 to $20 (backend stripeOrders.validServiceFee).
  const max = (k, kind) => (kind === "percent" ? 100 : k === "serviceFee" ? 20 : 10000);
  const invalid = FIELDS.some(([k, , kind]) => form[k] === "" || !Number.isFinite(fees[k]) || fees[k] < 0 || fees[k] > max(k, kind));
  const changedFromSaved = FIELDS.some(([k]) => fees[k] !== Number(saved[k]));
  const below = (k) => FLOOR.includes(k) && fees[k] < Number(s.standardFees[k]);
  const needsApproval = FIELDS.some(([k]) => below(k) || (!FLOOR.includes(k) && fees[k] !== Number(s.standardFees[k])));
  const agent = s.role === "AGENT";
  const label = PRICING[s.pricing] || PRICING.STANDARD;

  const next = async () => {
    if (!s.canChangeFees || !changedFromSaved) return onNext();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api("PUT", "/staff/fees", { token, body: { fees } }));
      onNext();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const field = ([k, name, kind], sm) => {
    const off = form[k] !== "" && num(form[k]) !== Number(s.standardFees[k]);
    const std = show(kind, s.standardFees[k]);
    const hint = agent && FLOOR.includes(k)
      ? (form[k] !== "" && below(k) ? `Below the ${std} minimum: needs approval` : `Minimum ${std}`)
      : off ? (agent ? `Standard ${std}: changing it needs approval` : `Standard: ${std}`) : " ";
    return (
      <Grid item xs={12} sm={sm} key={k}>
        <FieldLabel htmlFor={`rmf-fee-${k}`} label={name} />
        <TextField
          fullWidth size="small" id={`rmf-fee-${k}`} value={form[k]} disabled={!s.canChangeFees}
          onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
          helperText={hint}
          error={agent && form[k] !== "" && below(k)}
          inputProps={{ inputMode: "decimal" }}
          InputProps={kind === "percent"
            ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
            : { startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={off ? { "& .MuiOutlinedInput-root": { background: "#FFF6E8" }, "& .MuiFormHelperText-root": { color: "#8A4B0F" } } : undefined}
        />
      </Grid>
    );
  };

  return (
    <StepCard
      title="Fees for this restaurant"
      subtitle={s.role === "ADMIN"
        ? "This restaurant's Schedule A fees. They apply as soon as you save, and the owner sees and signs them."
        : "Set this restaurant's fees. The order percentages, third-party % and service fee are minimums: higher applies right away. Going below a minimum, or changing any other fee, goes to a super admin for approval, and the application goes to the restaurant once it's approved."}
    >
      <ErrorBanner error={error} />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <Chip size="small" label={label[0]} sx={{ background: label[1], color: label[2], fontWeight: 600 }} />
        {s.requestNote && <Typography sx={{ color: T.error, fontSize: 14 }}>Not approved: {s.requestNote}</Typography>}
      </Box>
      {!s.canChangeFees && (
        <Alert severity="info" icon={<Icon.Info size={20} />} sx={{ mb: 2 }}>
          {view.status !== "DRAFT"
            ? "This application is signed, so its fees can no longer change."
            : "It has already gone to the restaurant. Ask a super admin to change its fees."}
        </Alert>
      )}
      <SubHeading first>Order percentage (% of subtotal)</SubHeading>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ mb: 1 }}>{FIELDS.filter(([k]) => ORDER.includes(k)).map((f) => field(f, 4))}</Grid>
      <SubHeading>Third-party orders and service fee</SubHeading>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ mb: 1 }}>{FIELDS.filter(([k]) => PLATFORM.includes(k)).map((f) => field(f, 6))}</Grid>
      <SubHeading>Other fees</SubHeading>
      <Grid container rowSpacing={1} columnSpacing={2}>{FIELDS.filter(([k]) => !FLOOR.includes(k)).map((f) => field(f, 6))}</Grid>
      {s.canChangeFees && agent && changedFromSaved && needsApproval && (
        <Alert severity="warning" icon={<Icon.Alert size={20} />} sx={{ mt: 1 }}>Saving sends these fees to a super admin for approval. The restaurant can’t sign until they’re approved.</Alert>
      )}
      <Typography sx={{ color: T.hint, fontSize: 14, mt: 2 }}>
        The owner accepts the authorizations (sign-up, credit check, personal guarantee, electronic signature) when they review and sign.
      </Typography>
      <CardFooter
        left={onBack ? <GhostButton onClick={onBack} startIcon={<Icon.ArrowLeft size={16} />}>Back</GhostButton> : null}
        right={(
          <PrimaryButton onClick={next} busy={busy} disabled={invalid}>
            {s.canChangeFees && changedFromSaved ? "Save fees & continue" : "Continue"}
          </PrimaryButton>
        )}
      />
    </StepCard>
  );
}

/** The last step for staff: send it to the restaurant to review and sign. */
export function StaffSendStep({ view, token, onSaved, onBack, onGoTo }) {
  const s = view.staff;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // What the owner will still be asked for (the authorizations are always theirs).
  const missing = (view.missing || []).filter((m) => m.step !== "terms");
  const sent = !s.held;
  const waiting = s.held && s.sendRequestedAt && s.pricing === "PENDING";

  const send = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api("POST", "/staff/send", { token, body: {} });
      onSaved(res);
      setNotice(res.result === "SENT"
        ? `Sent. ${s.contactEmail} was emailed a link to review and sign.`
        : `Saved for sending. It goes to ${s.contactEmail} as soon as a super admin approves the fees.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard title="Send for signature" subtitle={`The owner gets a link to review the application, complete anything missing, accept the terms and sign.`}>
      <ErrorBanner error={error} />
      {notice && <Alert severity="success" icon={<Icon.Check size={20} />} sx={{ mb: 2 }}>{notice}</Alert>}
      {sent && !notice && <Alert severity="success" icon={<Icon.Check size={20} />} sx={{ mb: 2 }}>Sent to {s.contactEmail}{s.inviteSentAt ? ` on ${day(s.inviteSentAt)}` : ""}.</Alert>}
      {waiting && !notice && (
        <Alert severity="warning" icon={<Icon.Alert size={20} />} sx={{ mb: 2 }}>
          Waiting for a super admin to approve the fees. It goes to {s.contactEmail} as soon as they're approved.
        </Alert>
      )}
      {!sent && !waiting && s.pricing === "PENDING" && (
        <Alert severity="info" icon={<Icon.Info size={20} />} sx={{ mb: 2 }}>
          The fees are waiting for a super admin. Send it now and it goes to {s.contactEmail} as soon as they're approved.
        </Alert>
      )}

      <SubHeading first>{missing.length ? "The owner will be asked to complete" : "Everything is filled in"}</SubHeading>
      {missing.length ? (
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 2, border: `1px solid ${T.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          {missing.map((m, i) => (
            <Box component="li" key={m.message} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.25, borderTop: i ? `1px solid ${T.divider}` : 0 }}>
              <Typography sx={{ flex: "1 1 200px", fontSize: 15, color: T.text }}>{m.message}</Typography>
              <Button
                onClick={() => onGoTo(m.step)}
                endIcon={<Icon.ArrowRight size={16} />}
                sx={{ color: T.link, fontWeight: 600, px: 1.25, "&:hover": { color: T.linkHover, background: T.redTint } }}
              >
                Fill it in
              </Button>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ color: T.text2, fontSize: 15, mb: 2 }}>The owner only needs to review it, accept the terms and sign.</Typography>
      )}

      <CardFooter
        left={onBack ? <GhostButton onClick={onBack} startIcon={<Icon.ArrowLeft size={16} />}>Back</GhostButton> : null}
        right={(
          <PrimaryButton onClick={send} busy={busy} disabled={waiting && !sent} sx={{ maxWidth: "100%", whiteSpace: "normal", textAlign: "center" }}>
            {sent ? "Send the link again" : `Send to ${s.contactEmail} for signature`}
          </PrimaryButton>
        )}
        hint="You can close this tab when you're done; the application is saved."
      />
    </StepCard>
  );
}
